import { useState, useCallback } from 'react'
import { Box, LinearProgress, Typography, Switch, FormControlLabel, Paper, useTheme } from '@mui/material'
import { ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import _ from 'lodash'
import { useTimelineStyles } from '~/crop/crop-schedule/hooks/use-timeline-styles'
import { useCropPhaseData } from './use-crop-phase-data'

const FALLBACK_COLORS = ['#6366f1', '#14b8a6', '#f43f5e', '#a855f7', '#0ea5e9']
const RADIAN = Math.PI / 180

export const CropPhaseChart = () => {
  const [showPlants, setShowPlants] = useState(false)
  const [hoveredEntry, setHoveredEntry] = useState(null)
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 })
  const { phaseData, loading } = useCropPhaseData()
  const theme = useTheme()
  const { getPhaseStyle } = useTimelineStyles()

  const phaseColor = useCallback((code, idx) =>
    getPhaseStyle(code).color || FALLBACK_COLORS[idx % FALLBACK_COLORS.length],
    [getPhaseStyle]
  )
  
  const countKey = showPlants ? 'plantCount' : 'cropCount'

  // Label: icon inside slice if large enough, callout arrow+icon for small slices
  const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
    const entry = phaseData[index]
    if (!entry) return null
    const style = getPhaseStyle(entry.phaseCode)

    const cos = Math.cos(-RADIAN * midAngle)
    const sin = Math.sin(-RADIAN * midAngle)

    const midRadius = (innerRadius + outerRadius) / 2
    const arcLength = percent * 2 * Math.PI * midRadius

    if (arcLength > 20) {
      return (
        <text
          key={`icon-${index}`}
          x={cx + midRadius * cos}
          y={cy + midRadius * sin}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={11}
          pointerEvents="none"
        >
          {style.icon}
        </text>
      )
    }

    const x1 = cx + (outerRadius + 3) * cos
    const y1 = cy + (outerRadius + 3) * sin
    const x2 = cx + (outerRadius + 18) * cos
    const y2 = cy + (outerRadius + 18) * sin

    return (
      <g key={`callout-${index}`}>
        <line
          x1={x1} y1={y1}
          x2={x2} y2={y2}
          stroke={phaseColor(entry.phaseCode, index)}
          strokeWidth={1}
          opacity={0.7}
        />
        <text
          x={x2}
          y={y2}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={11}
        >
          {style.icon}
        </text>
      </g>
    )
  }

  if (loading) return <LinearProgress />

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, mb: 0.25 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.65rem', letterSpacing: '0.05em' }}>
          CROP PHASES
        </Typography>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showPlants}
              onChange={(_, v) => setShowPlants(v)}
              sx={{ transform: 'scale(0.7)', ml: 0 }}
            />
          }
          label={
            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
              {showPlants ? 'Plants' : 'Crops'}
            </Typography>
          }
          labelPlacement="start"
          sx={{ m: 0, gap: 0 }}
        />
      </Box>

      {/* Pie with callout labels and cursor-following hover tooltip */}
      <Box
        sx={{ flex: 1, minHeight: 0, position: 'relative' }}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect()
          setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
        }}
      >
        {hoveredEntry && (
          <Paper
            elevation={2}
            sx={{
              position: 'absolute',
              top: tooltipPos.y,
              left: tooltipPos.x,
              transform: 'translate(-50%, calc(-100% - 8px))',
              px: 1,
              py: 0.4,
              borderRadius: 1,
              zIndex: 10,
              pointerEvents: 'none',
              minWidth: 80,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', fontSize: '0.65rem' }}>
              {hoveredEntry.label}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.65rem' }}>
              {showPlants ? 'Plants' : 'Crops'}: {hoveredEntry[countKey]}
            </Typography>
          </Paper>
        )}

        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            {phaseData.length === 0 ? (
              <Pie
                data={[{ value: 1 }]}
                dataKey="value"
                cx="50%"
                cy="50%"
                innerRadius="25%"
                outerRadius="80%"
                isAnimationActive={false}
                labelLine={false}
              >
                <Cell fill="transparent" stroke={theme.palette.primary.main} strokeWidth={1} strokeOpacity={0.35} />
              </Pie>
            ) : (
              <Pie
                data={phaseData}
                dataKey={countKey}
                nameKey="label"
                cx="50%"
                cy="50%"
                innerRadius="25%"
                outerRadius="80%"
                strokeWidth={0}
                isAnimationActive={false}
                label={renderLabel}
                labelLine={false}
                onMouseEnter={(entry) => setHoveredEntry(entry)}
                onMouseLeave={() => setHoveredEntry(null)}
              >
                {phaseData.map((entry, idx) => {
                  const color = phaseColor(entry.phaseCode, idx)
                  const baseOpacity = hoveredEntry?.phaseCode === entry.phaseCode ? 0.75 : hoveredEntry ? 0.35 : 0.55
                  return (
                    <Cell
                      key={entry.phaseCode}
                      fill={color}
                      fillOpacity={baseOpacity}
                      stroke={color}
                      strokeWidth={1}
                      strokeOpacity={0.8}
                    />
                  )
                })}
              </Pie>
            )}
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  )
}
